import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import { createStyles, palette, spacing } from "../../styles/theme";
import { createMaskOverlay } from "./compositorEngine";
import {
  normalizeSpecsImageUploadFile,
  readSpecsImageBlobAsDataUrl,
} from "./specsImageUpload";
import { useSpecsEditorContextValue } from "./useSpecsEditor";

const COMPOSITE_PREVIEW_HEIGHT = "calc(100vh - 200px)";

const styles = createStyles({
  debugToggleButton: {
    position: "absolute",
    top: 8,
    right: 8,
    opacity: 0,
    transition: "opacity 120ms ease-in-out",
    bgcolor: "rgba(255,255,255,0.88)",
    boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
    "&:hover": {
      bgcolor: "rgba(255,255,255,0.96)",
    },
    "&:focus-visible": {
      opacity: 1,
    },
  },
});

function MaskOverlay({ url }: { url: string }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    createMaskOverlay(url).then((result) => {
      if (!cancelled) {
        setDataUrl(result);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!dataUrl) return null;

  return (
    <Box
      component="img"
      src={dataUrl}
      alt="Hovered layer"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        objectFit: "contain",
        borderRadius: "4px",
        pointerEvents: "none",
      }}
    />
  );
}

type CompositeDebugStep = {
  label: string;
  dataUrl: string;
  debugUrl?: string | null;
};

function CompositeDebugSteps({
  label,
  steps,
}: {
  label: string;
  steps: CompositeDebugStep[];
}) {
  return (
    <Box>
      <Typography
        variant="caption"
        sx={{ fontWeight: 600, mb: 0.5, display: "block" }}
      >
        {label}
      </Typography>
      <Stack spacing={1}>
        {steps.map((step, idx) => (
          <Box key={`${label}-${idx}`} sx={{ textAlign: "center" }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mb: 0.5, display: "block", textAlign: "left" }}
            >
              {idx + 1}. {step.label}
              {step.debugUrl ? (
                <Button
                  component="a"
                  href={step.debugUrl}
                  target="_blank"
                  rel="opener"
                  size="small"
                  variant="text"
                  sx={{
                    ml: 1,
                    minWidth: 0,
                    p: 0,
                    fontSize: "10px",
                    lineHeight: 1,
                    textTransform: "none",
                    verticalAlign: "baseline",
                  }}
                >
                  WebGL debug
                </Button>
              ) : null}
            </Typography>
            <Box
              component="img"
              src={step.dataUrl}
              alt={step.label}
              sx={{
                width: "auto",
                height: "420px",
                bgcolor: palette.grey.lightBackground,
                borderRadius: "4px",
              }}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export function CompositePreviewSection() {
  const {
    editor,
    effectiveLayerView,
    globalPreviewComposite,
    hoveredMaskUrl,
    previewComposite,
  } = useSpecsEditorContextValue();
  const preview =
    effectiveLayerView === "global" ? globalPreviewComposite : previewComposite;
  const referenceImageUrl = editor.session.referenceImageUrl;
  const hasLayers = editor.allOutputImages.length > 0;
  const { front, back, showBack } = preview;
  const [frontDebugOpen, setFrontDebugOpen] = useState(false);
  const [backDebugOpen, setBackDebugOpen] = useState(false);
  const hasFrontDebugSteps = front.steps.length > 0;
  const hasBackDebugSteps = back.steps.length > 0;
  const visibleDebugSectionCount =
    (frontDebugOpen && hasFrontDebugSteps ? 1 : 0) +
    (backDebugOpen && hasBackDebugSteps ? 1 : 0);

  const handleReferenceFile = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const { file: displayableFile } = await normalizeSpecsImageUploadFile(
      file,
      {
        normalizeOversizedRaster: false,
      }
    );
    editor.setReferenceImageUrl(
      await readSpecsImageBlobAsDataUrl(displayableFile, displayableFile.name)
    );
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Button variant="outlined" size="small" component="label">
          {referenceImageUrl ? "Changer la référence" : "Charger une référence"}
          <input
            type="file"
            accept=".png,.jpg,.jpeg,.webp,.psd"
            onChange={handleReferenceFile}
            style={{ display: "none" }}
          />
        </Button>
        {referenceImageUrl && (
          <Button
            size="small"
            color="error"
            onClick={() => editor.setReferenceImageUrl(null)}
          >
            Supprimer la référence
          </Button>
        )}
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: referenceImageUrl ? "1fr 1fr" : "1fr",
          gap: spacing.groupingDesktop,
        }}
      >
        <Box>
          <Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
            Composite
          </Typography>
          <Stack direction="row" spacing={1}>
            <Box
              data-testid="front-composite-panel"
              sx={{
                flex: 1,
                bgcolor: palette.grey.lightBackground,
                borderRadius: "4px",
                height: COMPOSITE_PREVIEW_HEIGHT,
                minHeight: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {front.url ? (
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    "&:hover .composite-debug-toggle": {
                      opacity: 1,
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={front.url}
                    alt="Front composite"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      borderRadius: "4px",
                    }}
                  />
                  {hasFrontDebugSteps && (
                    <Tooltip title="Compositing steps">
                      <IconButton
                        className="composite-debug-toggle"
                        aria-label="Toggle front compositing steps"
                        size="small"
                        onClick={() => setFrontDebugOpen((open) => !open)}
                        sx={styles.debugToggleButton}
                      >
                        <SettingsOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hoveredMaskUrl && <MaskOverlay url={hoveredMaskUrl} />}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {hasLayers ? "Traitement..." : "Aucun layer à composer"}
                </Typography>
              )}
            </Box>
            {showBack && (
              <Box
                data-testid="back-composite-panel"
                sx={{
                  flex: 1,
                  bgcolor: palette.grey.lightBackground,
                  borderRadius: "4px",
                  height: COMPOSITE_PREVIEW_HEIGHT,
                  minHeight: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {back.url ? (
                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                      "&:hover .composite-debug-toggle": {
                        opacity: 1,
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src={back.url}
                      alt="Back composite"
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        borderRadius: "4px",
                      }}
                    />
                    {hasBackDebugSteps && (
                      <Tooltip title="Compositing steps">
                        <IconButton
                          className="composite-debug-toggle"
                          aria-label="Toggle back compositing steps"
                          size="small"
                          onClick={() => setBackDebugOpen((open) => !open)}
                          sx={styles.debugToggleButton}
                        >
                          <SettingsOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Traitement...
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        </Box>

        {referenceImageUrl && (
          <Box>
            <Typography variant="caption" sx={{ mb: 0.5, display: "block" }}>
              Référence
            </Typography>
            <Box
              sx={{
                bgcolor: palette.grey.lightBackground,
                borderRadius: "4px",
                minHeight: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Box
                component="img"
                src={referenceImageUrl}
                alt="Reference"
                sx={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "calc(100vh - 200px)",
                  objectFit: "contain",
                  borderRadius: "4px",
                }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {(hasFrontDebugSteps || hasBackDebugSteps) && (
        <Collapse in={visibleDebugSectionCount > 0} mountOnEnter unmountOnExit>
          <Box sx={{ p: 1 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns:
                  visibleDebugSectionCount > 1 ? "1fr 1fr" : "1fr",
                gap: 2,
              }}
            >
              {frontDebugOpen && hasFrontDebugSteps && (
                <CompositeDebugSteps label="Front" steps={front.steps} />
              )}
              {backDebugOpen && hasBackDebugSteps && (
                <CompositeDebugSteps label="Back" steps={back.steps} />
              )}
            </Box>
          </Box>
        </Collapse>
      )}
    </Box>
  );
}
